/*
 * A read with an honest state machine.
 *
 * Four states, not three, and the fourth is the point: a tab whose endpoint is not
 * deployed yet gets a 404, and a 404 rendered as an empty panel is
 * indistinguishable from a working tab with no data. That ambiguity is what wastes
 * a reviewer's time, so `unavailable` is kept separate from `ready` with an empty
 * body and the failing path travels with it.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../../../api/client.ts'

export type ResourceStatus = 'loading' | 'ready' | 'unavailable' | 'error'

export interface Resource<T> {
  status: ResourceStatus
  data: T | null
  error: Error | null
  /** HTTP status when the failure came from the API rather than the network. */
  httpStatus: number | null
  /** True while a reload is in flight over data that is already on screen. */
  refreshing: boolean
  reload: () => void
}

/**
 * Run `load` on mount and whenever `key` changes.
 *
 * `key` is a string rather than a dependency array so a caller cannot accidentally
 * pass an inline object and re-fetch on every render — the commonest way this kind
 * of hook turns into a request loop.
 */
export function useResource<T>(key: string, load: () => Promise<T>): Resource<T> {
  const [status, setStatus] = useState<ResourceStatus>('loading')
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [httpStatus, setHttpStatus] = useState<number | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [nonce, setNonce] = useState(0)
  const loadRef = useRef(load)
  loadRef.current = load
  const hasData = useRef(false)
  const loadedKey = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // A different key is a different resource: drop what is on screen rather than
    // leaving another base's data visible under a fresh heading.
    if (loadedKey.current !== key) {
      loadedKey.current = key
      hasData.current = false
      setData(null)
    }
    if (hasData.current) setRefreshing(true)
    else setStatus('loading')

    void (async () => {
      try {
        const result = await loadRef.current()
        if (cancelled) return
        hasData.current = true
        setData(result)
        setError(null)
        setHttpStatus(null)
        setStatus('ready')
      } catch (err) {
        if (cancelled) return
        const failure = err instanceof Error ? err : new Error('запрос не удался')
        setError(failure)
        if (err instanceof ApiError) {
          setHttpStatus(err.status)
          // 404 covers three things the API does not distinguish: the route does
          // not exist, the base is not visible, and the feature is switched off
          // for the account. All three mean "there is nothing to show and it is
          // not because the base is empty", which is what `unavailable` says.
          setStatus(err.status === 404 || err.status === 501 ? 'unavailable' : 'error')
        } else {
          setHttpStatus(null)
          setStatus('error')
        }
      } finally {
        if (!cancelled) setRefreshing(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [key, nonce])

  const reload = useCallback(() => setNonce((value) => value + 1), [])

  return { status, data, error, httpStatus, refreshing, reload }
}
